'use client';

import {
  ReactNode,
  useRef,
  useContext,
  useMemo,
  useState,
  type FC,
} from 'react';
import {
  Column,
  Flex,
  InlineUnsafe,
  Loading,
  Separator,
  Text,
  Icon,
  ButtonGroup,
  Button,
} from '@/_components';
import styles from './QuestionnaireLetterSection.module.css';
import type { EngagementSignature } from '@/_types/models/questionnaire';
import type { QuestionnaireLetterSectionProps } from './QuestionnaireLetterSection.types';
import { getClasses } from '@/_utils/classnames';
import { ClientQuestionnaireContext } from '@/_contexts/client/ClientQuestionnaireProvider';
import { SignaturePad } from './_components/SignaturePad';
import { SignatureLocked } from './_components/SignatureLocked';
import { SignatureNotNeeded } from './_components/SignatureNotNeeded';
import { UserContext } from '@/_contexts/UserProvider';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { SignatureCompleted } from './_components/SignatureCompleted';
import { OtherSignatures } from './_components/OtherSignatures';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { useScrollDown } from '@/_hooks/useScrollDown';
import { NoSignatureRequested } from './_components/NoSignatureRequested';
import { OtherSignatureRequireToSign } from './_components/OtherSignatureRequireToSign';

export const QuestionnaireLetterSection: FC<
  QuestionnaireLetterSectionProps
> = ({ section, mode }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { user, isAdvisor } = useContext(UserContext);
  const {
    selectedSignatures,
    reloadQuestionnaires,
    reloadQuestions,
    selectedQuestionnaire,
    isLoading,
  } = useContext(ClientQuestionnaireContext);
  const { previewQuestionnaire, selectedPreviewSignatures } =
    useContext(PreviewContext);
  const { isScrollable, isScrolledDown, scrollDown } = useScrollDown(
    containerRef,
    360,
  );

  const { preferences } = useContext(FirmSettingsContext);
  const [isSigning, setSigning] = useState<boolean>(false);

  const sectionSignatures = selectedSignatures.filter(
    (sig) => sig.section_id === section?.id,
  );

  const signatures: Array<EngagementSignature> = useMemo(() => {
    if (mode === 'admin-preview') return selectedPreviewSignatures;
    if (mode === 'admin-template') {
      return [
        {
          client_time: new Date().toISOString(),
          id: -1,
          ip_address: '127.0.0.1',
          signature: 'Your Client',
          signed_at: new Date().toISOString(),
          user_agent: '',
          user_id: -1,
          user_name: 'Your Client',
          section_id: -1,
        },
      ];
    }

    return sectionSignatures;
  }, [mode, sectionSignatures, selectedPreviewSignatures]);

  const activeSignature: EngagementSignature | undefined = useMemo(() => {
    return preferences?.require_one_signature_per_section
      ? signatures?.[0]
      : signatures.find((signature) => signature.user_id === user?.id);
  }, [signatures, preferences?.require_one_signature_per_section, user?.id]);

  const isLocked: boolean = useMemo(() => {
    if (mode === 'admin-preview') {
      return Boolean(
        previewQuestionnaire?.locked || previewQuestionnaire?.completed,
      );
    }
    if (mode === 'admin-template') return true;

    return Boolean(
      selectedQuestionnaire?.locked || selectedQuestionnaire?.completed,
    );
  }, [
    mode,
    selectedQuestionnaire?.locked,
    selectedQuestionnaire?.completed,
    previewQuestionnaire?.completed,
    previewQuestionnaire?.locked,
  ]);

  const onSigned = async () => {
    await Promise.all([reloadQuestionnaires(), reloadQuestions()]);
  };

  // TODO(API): @chris Should only have one version coming from api
  const sectionCustomElement: string | undefined =
    section?.custom_element || section?.customElement;

  const getSignatureComponent = (): ReactNode => {
    if (isSigning && activeSignature) {
      return (
        <SignaturePad
          onSigned={onSigned}
          requiredSignature={activeSignature}
          isLoading={isSigning}
          setLoading={setSigning}
        />
      );
    }

    if (mode === 'client' && isLoading) return <Loading size={16} />;
    if (mode !== 'client') {
      return <OtherSignatures mode={mode} signatures={signatures} />;
    }
    if (isLocked) return <SignatureLocked />;
    if (!section?.engagement_signatures?.length) {
      return <NoSignatureRequested />;
    }
    if (section?.marked_completed && isAdvisor) return <SignatureNotNeeded />;

    // show signature
    if (!!activeSignature?.signature) {
      return <SignatureCompleted signature={activeSignature} />;
    }

    // ask for signature
    if (activeSignature || preferences?.require_one_signature_per_section) {
      return (
        <SignaturePad
          onSigned={onSigned}
          requiredSignature={activeSignature}
          isLoading={isSigning}
          setLoading={setSigning}
        />
      );
    }

    return <OtherSignatureRequireToSign />;
  };

  const signatureComponent: ReactNode = getSignatureComponent();

  const signatureHelperClasses: string = getClasses(
    styles.questionnaireLetter__signature__button,
    {
      [styles.questionnaireLetter__signature__buttonHidden]: isScrolledDown,
    },
  );

  return (
    <Flex
      isColumn={true}
      className={styles.questionnaireLetter}
      gap={12}
      ref={containerRef}
    >
      <Flex isColumn={true} gap={12} padding={12}>
        {section?.instruction && (
          <>
            <Text type="div">
              <InlineUnsafe
                text={section?.instruction}
                isExtremelyUnsafe={true}
              />
            </Text>
            <Separator isLight={true} />
          </>
        )}
        {sectionCustomElement && (
          <Text type="div">
            <InlineUnsafe
              text={sectionCustomElement}
              isExtremelyUnsafe={true}
            />
          </Text>
        )}
      </Flex>
      {isScrollable && (
        <div className={signatureHelperClasses}>
          <ButtonGroup align="center" isFullWidth={true}>
            <Button
              intent="secondary"
              value="Sign"
              iconLeft="arrow-down"
              onClick={scrollDown}
            />
          </ButtonGroup>
        </div>
      )}
      <Flex
        isCentered={true}
        padding={8}
        className={styles.questionnaireLetter__signature__container}
      >
        <Column
          width={460}
          isColumnCentered={true}
          isCentered={true}
          gap={10}
          className={styles.questionnaireLetter__signature}
        >
          {signatureComponent}
          {sectionSignatures?.length >= 1 && (
            <>
              {preferences.require_one_signature_per_section && (
                <>
                  <Flex gap={4}>
                    <Icon size={20} name="info" color="secondary" />
                    <Text type="tiny" color="secondary">
                      Only one signature is required. Signatures below will be
                      marked automatically as Not Needed after you sign.
                    </Text>
                  </Flex>
                </>
              )}
              <OtherSignatures signatures={sectionSignatures} mode={mode} />
            </>
          )}
        </Column>
      </Flex>
    </Flex>
  );
};
